import { useEffect, useRef, useState, useCallback } from "react";
import { userIdentity } from "./identity";
import { notificationAudio } from "./notificationSound";

/**
 * `useCitizenBroadcasts` — opens the citizen-side WebSocket and surfaces
 * an `incoming` broadcast (newest unread) plus a `dismiss()` callback
 * the in-app banner uses.
 *
 * Mounted ONCE at the top of the citizen shell (`App.js`) so a broadcast
 * banner appears no matter which page the user is on.
 *
 * Lifecycle:
 *   1. Wait for the user to have completed onboarding (phone set).
 *   2. Open WS to `/api/ws/citizen/{phone}`. Auto-reconnect with backoff.
 *   3. On `broadcast.created`: play the civil-defence sweep sound, store
 *      the broadcast in `incoming`. UI shows a slide-down banner.
 *   4. Banner auto-dismisses after 10 s OR when the user taps it.
 */

export function useCitizenBroadcasts({ enabled = true } = {}) {
  const [phone, setPhone] = useState(() => userIdentity.getPhone());
  const [incoming, setIncoming] = useState(null);
  const [live, setLive] = useState(false);
  const dismissTimerRef = useRef(null);

  useEffect(() => {
    return userIdentity.subscribe((p) => setPhone(p || ""));
  }, []);

  const wsRef = useRef(null);
  const retryRef = useRef(0);
  const timerRef = useRef(null);
  const closedRef = useRef(false);

  const handleBroadcast = useCallback((bc) => {
    setIncoming(bc);
    try { notificationAudio.playCitizenBroadcast(); } catch { /* noop */ }
    if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
    // 10-second visibility, then the banner slides up unless the user
    // tapped it (in which case we route to the detail page).
    dismissTimerRef.current = setTimeout(() => setIncoming(null), 10000);
  }, []);

  useEffect(() => {
    if (!enabled || !phone) return undefined;
    closedRef.current = false;

    const buildUrl = () => {
      const base = process.env.REACT_APP_BACKEND_URL || "";
      const wsBase = base.replace(/^http/i, "ws");
      return `${wsBase}/api/ws/citizen/${encodeURIComponent(phone)}`;
    };

    const open = () => {
      if (closedRef.current) return;
      let ws;
      try { ws = new WebSocket(buildUrl()); } catch (_e) { schedule(); return; }
      wsRef.current = ws;
      ws.onopen = () => { setLive(true); retryRef.current = 0; };
      ws.onmessage = (ev) => {
        let msg;
        try { msg = JSON.parse(ev.data); } catch { return; }
        if (msg?.type === "broadcast.created" && msg.broadcast) {
          handleBroadcast(msg.broadcast);
        }
      };
      ws.onclose = () => { setLive(false); if (!closedRef.current) schedule(); };
      ws.onerror = () => { try { ws.close(); } catch { /* noop */ } };
    };

    const schedule = () => {
      const delay = Math.min(1000 * Math.pow(2, retryRef.current), 30000);
      retryRef.current += 1;
      timerRef.current = setTimeout(open, delay);
    };

    open();

    return () => {
      closedRef.current = true;
      if (timerRef.current) clearTimeout(timerRef.current);
      if (wsRef.current) { try { wsRef.current.close(); } catch { /* noop */ } }
    };
  }, [enabled, phone, handleBroadcast]);

  const dismiss = useCallback(() => {
    setIncoming(null);
    if (dismissTimerRef.current) {
      clearTimeout(dismissTimerRef.current);
      dismissTimerRef.current = null;
    }
  }, []);

  return { incoming, dismiss, live };
}
